import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Public Visitor Tracker — pings backend every 15s while on a public page.
 * Stores session_id in sessionStorage so refreshes count as same visit.
 * Use it INSIDE PublicLayout / landing-only routes (not on /dashboard).
 */
export function PublicVisitorTracker() {
  const location = useLocation();
  const tickRef = useRef<NodeJS.Timeout | null>(null);
  const startedRef = useRef<number>(Date.now());
  const isNewRef = useRef<boolean>(false);

  useEffect(() => {
    const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
    if (!BACKEND) return;

    let sessionId = sessionStorage.getItem("grafton_visitor_sid");
    if (!sessionId) {
      sessionId = `vis_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      sessionStorage.setItem("grafton_visitor_sid", sessionId);
      isNewRef.current = true;
    }

    const ping = async (isNew = false) => {
      try {
        await fetch(`${BACKEND}/api/visitors/track`, {
          method: "POST",
          credentials: "omit",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionId,
            page: location.pathname + location.search,
            referrer: document.referrer || "",
            screen_width: window.innerWidth,
            duration_seconds: Math.floor((Date.now() - startedRef.current) / 1000),
            is_new: isNew,
          }),
          keepalive: true,
        });
      } catch {
        /* ignore network errors */
      }
    };

    // First ping immediately (marked as new if just created)
    ping(isNewRef.current);
    isNewRef.current = false;

    tickRef.current = setInterval(() => ping(false), 15000);
    return () => {
      if (tickRef.current) clearInterval(tickRef.current);
    };
  }, [location.pathname]);

  return null;
}
